import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { GradientBorder } from '@/components/ui/gradient-border';
import { SpriteSheetPlayer } from '@/components/ui/sprite-sheet-player';
import { crownBadgeData, crownBadgeSheet, crownFrames } from '@/lib/crown-frames';

export interface CrownButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Number of tickets / crowns to show next to the icon. Omit for an icon-only button. */
  count?: number;
  /**
   * Compact mode used when the home top bar is pinned to the viewport.
   * Shrinks pill height, the crown sprite, and the count typography.
   */
  compact?: boolean;
  /** Render without the gradient border ring. */
  noBorder?: boolean;
}

/**
 * Pill button that surfaces the user's crown reward count.
 *
 * Sits next to the coin balance in the lobby's top bar and links into
 * the crown reward program.
 *
 * @example
 * ```tsx
 * <CrownButton count={5} onClick={() => openRewards()} />
 * ```
 */
const CrownButton = forwardRef<HTMLButtonElement, CrownButtonProps>(
  ({ className, count, compact = false, noBorder = false, ...props }, ref) => {
    const wrapperClasses = cn(
      'inline-flex shrink-0 rounded-[47px] px-1',
      'transition-[height,opacity] duration-150 hover:opacity-90 active:opacity-80',
      compact ? 'h-9' : 'h-[46px]',
    );

    const button = (
      <button
        ref={ref}
        type="button"
        className={cn(
          'inline-flex h-full min-h-0 w-full min-w-0 items-center justify-center gap-1',
          'rounded-[45px] bg-transparent py-1.5',
          'cursor-pointer border-0',
          compact ? 'px-2' : 'px-3',
          noBorder && wrapperClasses,
          className,
        )}
        {...props}
      >
        <SpriteSheetPlayer
          spriteSheetSrc={crownBadgeSheet}
          sheetWidth={crownBadgeData.meta.size.w}
          sheetHeight={crownBadgeData.meta.size.h}
          frames={crownFrames}
          frameDurationMs={30}
          playback="pingpong"
          loopDelayMs={4000}
          width={34 * (compact ? 1 : 1.5)}
          height={27 * (compact ? 1 : 1.5)}
          className={cn('shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]', count != null && 'mr-[-5px]')}
        />
        {count != null && (
          <span
            className={cn(
              'font-semibold text-purple tracking-[-0.02em] leading-none transition-[font-size] duration-150',
              compact ? 'text-sm' : 'text-lg',
            )}
          >
            {count}
          </span>
        )}
      </button>
    );

    if (noBorder) return button;

    return <GradientBorder className={wrapperClasses}>{button}</GradientBorder>;
  },
);
CrownButton.displayName = 'CrownButton';

export { CrownButton };
